/**
 * ReviewStep
 *
 * Story 11: Registration Wizard - Review and Submit
 *
 * Step 5 of 5 in registration wizard
 *
 * Features:
 * - Display all collected data for review
 * - Edit buttons to go back to specific steps
 * - Terms and conditions checkbox
 * - Submit button
 * - Loading state during submission
 * - Error handling
 */

import { useState } from 'react';
import type { RegistrationFormData } from '../../types';

interface ReviewStepProps {
  data: RegistrationFormData;
  onBack: () => void;
  onEdit: (step: number) => void;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
}

export function ReviewStep({ data, onBack, onEdit, onSubmit, isSubmitting }: ReviewStepProps) {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!acceptedTerms) {
      setError('You must accept the terms and conditions');
      return;
    }

    setError(null);
    try {
      await onSubmit();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Review and Submit</h2>
        <p className="text-neutral-600">
          Please review your information before submitting your registration.
        </p>
      </div>

      <div className="space-y-6">
        {/* Invite Code Section */}
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-neutral-900">Invite Code</h3>
            <button
              type="button"
              onClick={() => onEdit(0)}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Edit
            </button>
          </div>
          <p className="text-neutral-700">{data.inviteCode}</p>
        </div>

        {/* Basic Information Section */}
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-neutral-900">Basic Information</h3>
            <button
              type="button"
              onClick={() => onEdit(1)}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Edit
            </button>
          </div>
          <dl className="space-y-2">
            <div>
              <dt className="text-sm font-medium text-neutral-600">Company Name</dt>
              <dd className="text-neutral-900">{data.companyName}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-neutral-600">Contact Email</dt>
              <dd className="text-neutral-900">{data.contactEmail}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-neutral-600">Contact Phone</dt>
              <dd className="text-neutral-900">{data.contactPhone}</dd>
            </div>
          </dl>
        </div>

        {/* Service Areas Section */}
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-neutral-900">Service Areas</h3>
            <button
              type="button"
              onClick={() => onEdit(2)}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Edit
            </button>
          </div>
          <dl className="space-y-2">
            <div>
              <dt className="text-sm font-medium text-neutral-600">Primary County</dt>
              <dd className="text-neutral-900">{data.primaryCounty}</dd>
            </div>
            {data.additionalCounties && data.additionalCounties.length > 0 && (
              <div>
                <dt className="text-sm font-medium text-neutral-600">Additional Counties</dt>
                <dd className="text-neutral-900">{data.additionalCounties.join(', ')}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-neutral-600">Service Types</dt>
              <dd className="text-neutral-900">{data.serviceTypes.join(', ')}</dd>
            </div>
          </dl>
        </div>

        {/* License Information Section */}
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold text-neutral-900">License & Certification</h3>
            <button
              type="button"
              onClick={() => onEdit(3)}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              Edit
            </button>
          </div>
          <dl className="space-y-2">
            <div>
              <dt className="text-sm font-medium text-neutral-600">License Number</dt>
              <dd className="text-neutral-900">{data.licenseNumber || 'Not provided'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-neutral-600">Insurance Information</dt>
              <dd className="text-neutral-900 whitespace-pre-wrap">{data.insuranceInfo || 'Not provided'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-neutral-600">Certifications</dt>
              <dd className="text-neutral-900 whitespace-pre-wrap">{data.certifications || 'Not provided'}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="border-t border-neutral-200 pt-6">
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            disabled={isSubmitting}
            className="checkbox mt-1"
          />
          <span className="text-sm text-neutral-700">
            I agree to the{' '}
            <a href="/terms" target="_blank" className="text-primary-600 hover:text-primary-700 underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" target="_blank" className="text-primary-600 hover:text-primary-700 underline">
              Privacy Policy
            </a>
            . I understand that my registration will be reviewed by county administrators before approval.
          </span>
        </label>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!acceptedTerms || isSubmitting}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              Submitting...
            </span>
          ) : (
            'Submit Registration'
          )}
        </button>
      </div>
    </div>
  );
}
